// tutara-frontend/src/components/auth/EnterpriseGate.tsx
import { type ReactNode } from 'react';
import { useEnterprisePlan } from '@/hooks/useEnterprisePlan';

interface EnterpriseGateProps {
  children: ReactNode;
  /** Rendered when the tenant is not on the ENTERPRISE plan. Defaults to null. */
  fallback?: ReactNode;
}

/**
 * Renders `children` only for tenants on the ENTERPRISE plan; otherwise renders
 * `fallback`. Role authorization is handled separately by `ProtectedRoute` —
 * this gate is purely the plan check (the frontend half of the backend
 * `EnterpriseFeatureGuard` defense-in-depth).
 *
 * As of Phase 1 the plan arrives in the login response (`user.subscriptionPlan`),
 * so `useEnterprisePlan` resolves synchronously at first render — no hold/timeout
 * is needed to avoid bouncing an enterprise admin to the fallback. We still guard
 * on `isLoading` (auth store rehydration) to avoid flashing the fallback.
 */
export function EnterpriseGate({ children, fallback = null }: EnterpriseGateProps) {
  const { isEnterprise, isLoading } = useEnterprisePlan();

  if (isLoading) return null;
  return <>{isEnterprise ? children : fallback}</>;
}

export default EnterpriseGate;
